import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { createPageUrl } from '@/utils';
import { notify } from '@/components/ui/notify';
import {
  fetchCompetitorEntitySignals,
  fetchPersonaCompetitiveProximity,
} from '@/lib/competitorsApi';
import {
  CompetitorBreakdownTable,
  CompetitorSignalRow,
  ProximityCard,
} from '@/components/competitors/competitorUi';
import { groupSignalsByCompetitor } from '@/lib/competitorSignals';
import { useTranslation, translate } from '@/components/utils/translations';

export default function PersonCompetitorsTabContent({
  tenantId,
  personId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState(null);
  const [signals, setSignals] = useState([]);

  useEffect(() => {
    if (!tenantId || !personId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [proximity, activityRaw] = await Promise.all([
          fetchPersonaCompetitiveProximity(tenantId, personId, {}),
          fetchCompetitorEntitySignals(
            'PersonaActivity',
            tenantId,
            { person_id: personId },
            '-date_published',
            200,
          ).catch(() => []),
        ]);

        if (cancelled) return;
        setRow(proximity?.row ?? null);
        setSignals(activityRaw || []);
      } catch (e) {
        if (!cancelled) {
          console.warn('Person competitors tab load failed:', e);
          if (e?.status >= 500) {
            notify({
              title: translate('error'),
              description: translate('acctCouldNotLoadCompetitorData'),
              variant: 'destructive',
            });
          }
          setRow(null);
          setSignals([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tenantId, personId]);

  const competitorGroups = useMemo(() => groupSignalsByCompetitor(signals), [signals]);

  const goToMarket = (competitorName) => {
    const params = new URLSearchParams({ tab: 'competitors' });
    if (competitorName) params.set('competitor', competitorName);
    navigate(createPageUrl(`Market?${params.toString()}`));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (!row) return null;

  return (
    <div className="space-y-6">
      <ProximityCard row={row} kind="persona" />

      {competitorGroups.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('acctCompetitorsLinkedToPersona')}</h3>
          <CompetitorBreakdownTable groups={competitorGroups} onRowClick={goToMarket} />
        </div>
      ) : null}

      {signals.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('acctSignalTimeline')}</h3>
          <div className="space-y-3">
            {signals.map((s) => (
              <CompetitorSignalRow key={s.id || `${s.summary}-${s.date_published}`} signal={s} sourceType="persona" />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
